import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { addRendition } from '@shared/utils/photoRendition';
import { HIGH_RES_RENDITION, buildLowResUrl } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto/renditions';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { getSsrHeroSizes } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto/ssrHeroSizes';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { getHeaderPhotoUrls } from './getHeaderPhotoUrls';

type SmartPreloadResult = Readonly<{
  criticalUrls: ReadonlyArray<string>;
  deferredUrls: ReadonlyArray<string>;
}>;

// Critical (current route) vs deferred (idle-callback) image set.
// Welcome route: header photos. Specific page: that page's photo.
// Honeymoon-redesign uses xlarge for the current page (LCP candidate, no crossfade).
//
// Invariant: a <link rel="preload"> that fetches one fixed URL while the real <img> resolves a
// *different* srcSet candidate silently wastes the preload (double fetch, no LCP benefit). Every
// critical/deferred URL this hook returns today is built at LOW_RES_RENDITION ('tiny', the
// crossfade's blur placeholder — a single fixed-size image by design) EXCEPT the
// honeymoon-redesign branch below, which uses HIGH_RES_RENDITION for SimpleModeHero, a component
// with no responsive srcSet to collide with. If a future change routes a `criticalUrls`/
// `deferredUrls` entry through OptimizedEventGuestSitePhoto's responsive high-res layer instead,
// give it matching `imagesrcset`/`imagesizes` on the `<link>` in PreloadImages.tsx — see
// useProgressiveImage.ts's `containerWidthPx` doc for why the two must stay in lockstep.
//
// Eager-SSR header layouts (see isEagerSsrHeader below) opt out of that <link> entirely: their
// sharp <img> is rendered directly in SSR markup with its own srcset/sizes, so the <img> itself
// IS the preload — the tiny placeholder's <link> is demoted out of criticalUrls instead, so it
// doesn't compete with the real LCP resource at fetchpriority=high.
export function useSmartPreloadImageUrls(
  pages: ReadonlyArray<EventPageFragment>,
  eventHandle: string,
  headerPhoto: SupportedHeaderPhotoLayoutSchema | null | undefined,
  fallbackPhotoUrl?: string | null,
  /**
   * Whether THIS website layout's header renderers actually pass `ssrSizes` down, i.e. whether an
   * eager sharp <img> really lands in the SSR markup.
   *
   * Required because `getSsrHeroSizes(layout)` alone is not that answer: it is keyed by
   * *presentation* layout, and `photoSlider` is offered by both Brannan and Aloha — but only
   * BrannanPhotoSlider wires `ssrSizes`. AlohaPhotoSlider does not, so keying the demotion on the
   * map alone drops an Aloha slider event's critical preload with no eager <img> to replace it.
   *
   * Defaults to `false` on purpose: a layout that forgets to opt in keeps its preload (a redundant
   * 514 B fetch at worst), whereas a layout wrongly opted in loses its LCP preload entirely.
   */
  rendersEagerSsrHero = false,
): SmartPreloadResult {
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  // checkIsWelcomePage is stable — useWelcomePage wraps it in useCallback
  const { checkIsWelcomePage } = useWelcomePage();
  const isStaysRedesignActive = useIsStaysRedesignActive();
  // Mirrors the exact gate OptimizedEventGuestSitePhoto uses for its own eager-SSR sharp <img>
  // (see isEagerSharpImage there) rather than re-reading the raw feature flag: skipInitialAnimation
  // is already false in admin preview (?preview=true) and on every client-side route change (no
  // SSR markup to get ahead of by then), which the raw flag alone doesn't capture.
  const skipInitialAnimation = useSkipInitialAnimation();
  // The eager sharp <img> only renders when the header layout has a first photo AND that photo
  // carries the crop the renderer will actually use — without one, OptimizedEventGuestSitePhoto
  // falls back to its CSS-background branch, which never renders the eager sharp <img> at all.
  // Duck-typed the same way getHeaderPhotoUrls does: not every layout in the union exposes
  // `photos`, and only the photoBackground pair carries `mobileCropInfo`.
  const firstHeaderPhoto =
    headerPhoto && 'photos' in headerPhoto ? (headerPhoto as { photos?: ReadonlyArray<{ cropInfo?: unknown; mobileCropInfo?: unknown }> }).photos?.[0] : undefined;

  // Same breakpoint pair PhotoBackground/PhotoBackgroundMonogram use for their own `isMobile`.
  // This is SSR-correct rather than a hydration-only value: useResponsive seeds itself from
  // DefaultMediaMatchProvider's request-User-Agent `defaultScreen`, so on a mobile UA the server
  // and the renderer agree here by construction — which is the whole point, since the preload
  // decision has to match the markup SSR actually emitted.
  const [isMobile = false] = useResponsive({ values: { xxs: true, sm: false } });

  // Mirrors PhotoBackgroundRenderer's `cropInfoToUse` exactly. Scoped to the two photoBackground
  // layouts on purpose: they are the ONLY renderers that consult mobileCropInfo, so widening this
  // to a blanket `cropInfo || mobileCropInfo` would wrongly mark a mobileCropInfo-only photo as
  // eager under photoTrio/photoSlider/photoArch — whose renderers pass `photo.cropInfo` straight
  // through — and DEMOTE a preload those layouts still need. Losing a critical preload is the
  // expensive direction; keeping a redundant one is not.
  const layout = headerPhoto?.presentationLayout;
  const usesMobileCrop = layout === 'photoBackground' || layout === 'photoBackgroundMonogram';
  const eagerCropInfo = usesMobileCrop && isMobile && firstHeaderPhoto?.mobileCropInfo ? firstHeaderPhoto.mobileCropInfo : firstHeaderPhoto?.cropInfo;
  // Eager-SSR layouts render the sharp <img> (the LCP resource) directly in SSR markup, where the
  // preload scanner discovers it natively. Keeping the tiny placeholder in criticalUrls would put
  // it at fetchpriority=high AHEAD of the LCP resource — demote it to the deferred set instead.
  // The blur layer still fetches immediately via its plain <img src> in the body. All three
  // conditions below must hold or there is no eager <img> to replace the demoted preload with: a
  // photo-less header, a header photo missing cropInfo, admin preview, and client-side navigation
  // all render the legacy hydration-gated path instead.
  const isEagerSsrHeader = rendersEagerSsrHero && skipInitialAnimation && getSsrHeroSizes(layout) !== undefined && !!eagerCropInfo;

  return useMemo(() => {
    const { allUrls: headerUrls, criticalCount: headerCriticalCount } = getHeaderPhotoUrls(headerPhoto);

    // Deferred-set URLs always at LOW_RES_RENDITION (do not swap to xlarge — bandwidth regression).
    const pagePhotoUrlsBySlug = new Map<string, string>();
    for (const page of pages) {
      if (page.photoV2?.url) {
        const url = buildLowResUrl(page.photoV2.url);
        pagePhotoUrlsBySlug.set(page.pageSlug, url);
      }
    }

    // Determine if user is on the welcome page
    const currentPage = currentPageSlug ? pages.find(p => p.pageSlug === currentPageSlug) : undefined;
    const isOnWelcomePage = !currentPageSlug || (currentPage ? checkIsWelcomePage(currentPage.pageSlug, currentPage.type).isWelcomePage : true);

    let criticalUrls: string[];
    let deferredUrls: string[];

    if (isOnWelcomePage) {
      // Welcome route: preload header photos. Welcome hero stays at tiny (crossfade preserved).
      // Eager-SSR headers skip criticalUrls entirely — see isEagerSsrHeader above.
      criticalUrls = isEagerSsrHeader ? [] : headerUrls.slice(0, headerCriticalCount);

      // Fallback for events with no active header (legacy Aloha or no header configured).
      // Guarded by !isEagerSsrHeader: when isEagerSsrHeader is true, the header layout has a
      // first photo with cropInfo, so its sharp <img> is already serving as the LCP resource —
      // this fallback must not re-promote the welcome page's photo into criticalUrls out from
      // under it. (A photo-less or cropInfo-less eager-*layout*, by contrast, leaves
      // isEagerSsrHeader false — see its definition above — so this fallback still runs for those.)
      if (criticalUrls.length === 0 && !isEagerSsrHeader) {
        const welcomePage = pages.find(p => checkIsWelcomePage(p.pageSlug, p.type).isWelcomePage);
        const welcomePhotoUrl = welcomePage?.photoV2?.url ?? fallbackPhotoUrl ?? null;
        if (welcomePhotoUrl) {
          criticalUrls = [buildLowResUrl(welcomePhotoUrl)];
        }
      }

      const remainingHeaderUrls = isEagerSsrHeader ? headerUrls : headerUrls.slice(headerCriticalCount);
      deferredUrls = [...remainingHeaderUrls, ...Array.from(pagePhotoUrlsBySlug.values())];
    } else {
      // Specific page route. Honeymoon-redesign uses xlarge (LCP, SimpleModeHero — no responsive
      // srcSet to collide with, see file-level comment) via addRendition. Every other page
      // preloads the tiny blur placeholder: route through buildLowResUrl (dialect-aware) instead
      // of addRendition — on /assets/public/ photos, addRendition's raw `rendition=tiny` param is
      // ignored by asset-optimizer and resolves to a different, auto-capped file than the
      // `resize=w48` URL the placeholder actually renders, silently double-fetching the photo.
      const currentPagePhotoUrl = currentPage?.photoV2?.url
        ? isStaysRedesignActive
          ? addRendition({ url: currentPage.photoV2.url, renditionSize: HIGH_RES_RENDITION })
          : buildLowResUrl(currentPage.photoV2.url)
        : undefined;
      const fallbackUrl = fallbackPhotoUrl
        ? isStaysRedesignActive
          ? addRendition({ url: fallbackPhotoUrl, renditionSize: HIGH_RES_RENDITION })
          : buildLowResUrl(fallbackPhotoUrl)
        : undefined;
      const photoToPreload = currentPagePhotoUrl || fallbackUrl;
      criticalUrls = photoToPreload ? [photoToPreload] : [];

      // Slug-based exclusion (not URL-string) so renditions don't collide for the same page.
      const otherPagePhotoUrls = currentPageSlug
        ? Array.from(pagePhotoUrlsBySlug.entries())
            .filter(([slug]) => slug !== currentPageSlug)
            .map(([, url]) => url)
        : Array.from(pagePhotoUrlsBySlug.values());
      deferredUrls = [...headerUrls, ...otherPagePhotoUrls];
    }

    // Deduplicate: remove from deferred anything already in critical
    const criticalSet = new Set(criticalUrls);
    deferredUrls = deferredUrls.filter(url => !criticalSet.has(url));

    return { criticalUrls, deferredUrls };
  }, [pages, currentPageSlug, headerPhoto, checkIsWelcomePage, fallbackPhotoUrl, isStaysRedesignActive, isEagerSsrHeader]);
}
